import { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Stack,
  Typography,
  IconButton,
} from '@mui/material';
import { Add, Close } from '@mui/icons-material';
import { Formik, Form } from 'formik';
import { useTranslationMessages } from 'lib/hooks/useTranslationMessages';
import { Reference, UseResumeDataQuery } from 'lib/hooks/useResumeDataQuery';
import { EditableCard } from 'components/resume-builder/ResumeEditor';
import * as Yup from 'yup';

interface EditingItem {
  index: number;
  item: Reference | null;
}

export default function ReferencesSection({
  section,
  formValues,
  updateFormValues,
}: {
  section: { type: 'references'; name: string; items: Reference[] };
  formValues: UseResumeDataQuery['data'];
  updateFormValues: (newValues: UseResumeDataQuery['data']) => void;
}) {
  const { t } = useTranslationMessages();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<EditingItem>({ index: -1, item: null });

  // Create validation schema with translated messages
  const referenceSchema = Yup.object().shape({
    name: Yup.string().required(t('createResume.references.validation.nameRequired')),
    phone_number: Yup.string().matches(/^\d{10}$/, t('createResume.references.validation.phoneInvalid')),
    type: Yup.mixed<'personal' | 'professional'>()
      .oneOf(['personal', 'professional'], t('createResume.references.validation.typeRequired'))
      .required(t('createResume.references.validation.typeRequired')),
    title_or_role: Yup.string(),
    employer: Yup.string(),
    email: Yup.string().email(t('createResume.references.validation.emailInvalid')),
  });

  const handleOpenDialog = (ref?: Reference, index?: number) => {
    setEditingItem(ref && index !== undefined ? { index, item: ref } : { index: -1, item: null });
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingItem({ index: -1, item: null });
  };

  const handleSubmit = (values: Reference) => {
    const prev = structuredClone(formValues);
    const sectionIndex = prev.sections.findIndex((s) => s.type === 'references');
    const referenceItems = prev.sections[sectionIndex].items as Reference[];

    if (editingItem.index !== -1) {
      referenceItems[editingItem.index] = values;
    } else {
      referenceItems.push(values);
    }

    prev.sections[sectionIndex].items = referenceItems;
    updateFormValues(prev);
    handleCloseDialog();
  };

  return (
    <Box mb={2}>
      <Typography variant="h4" component="h2">
        {t('createResume.references.title')}
      </Typography>

      <Typography component="p" variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>
        {t('createResume.references.description')}
      </Typography>

      <Stack spacing={1} mb={1}>
        {section.items.map((ref: Reference, index: number) => (
          <EditableCard
            key={`${ref.name}-${ref.phone_number}-${ref.email ?? ''}`}
            title={ref.name}
            subtitle={''}
            chipLabel={
              ref.type
                ? ref.type === 'professional'
                  ? t('createResume.references.chipProfessional')
                  : t('createResume.references.chipPersonal')
                : undefined
            }
            onEdit={() => handleOpenDialog(ref, index)}
            onDelete={() => {
              const prev = structuredClone(formValues);
              const sectionIndex = prev.sections.findIndex((s) => s.type === 'references');
              if (sectionIndex !== -1) {
                const referenceItems = prev.sections[sectionIndex].items as Reference[];
                prev.sections[sectionIndex].items = referenceItems.filter((_, i) => i !== index);
                updateFormValues(prev);
              }
            }}
          />
        ))}
      </Stack>

      <Button startIcon={<Add />} onClick={() => handleOpenDialog()} id="add-reference-resume-button">
        {t('createResume.references.addButton')}
      </Button>

      {/* Dialog for Add/Edit Reference */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog}>
        <Box sx={{}}>
          <IconButton
            aria-label={t('createResume.modal.closeLabel')}
            onClick={handleCloseDialog}
            sx={{
              position: 'absolute',
              right: 6,
              top: 6,
              color: 'black',
              fontSize: 34,
            }}
          >
            <Close />
          </IconButton>

          <DialogTitle>
            {editingItem.index !== -1
              ? t('createResume.references.dialog.titleEdit')
              : t('createResume.references.dialog.titleAdd')}
          </DialogTitle>

          <Formik
            initialValues={
              editingItem.item || {
                name: '',
                phone_number: '',
                type: '',
                title_or_role: '',
                employer: '',
                email: '',
              }
            }
            validationSchema={referenceSchema}
            onSubmit={handleSubmit}
          >
            {({ values, handleChange, errors, touched }) => (
              <Form>
                <DialogContent>
                  <Stack spacing={2} sx={{ mt: 1, minWidth: 400 }}>
                    <TextField
                      label={t('createResume.references.fields.type')}
                      name="type"
                      select
                      value={values.type || ''}
                      onChange={handleChange}
                      error={touched.type && Boolean(errors.type)}
                      helperText={touched.type && errors.type}
                      required
                      sx={(theme) => ({
                        '& .MuiFormLabel-asterisk': {
                          color: theme.palette.error.main,
                        },
                      })}
                    >
                      <MenuItem value="personal">{t('createResume.references.options.personal')}</MenuItem>
                      <MenuItem value="professional">{t('createResume.references.options.professional')}</MenuItem>
                    </TextField>
                    <TextField
                      label={t('createResume.references.fields.name')}
                      name="name"
                      value={values.name}
                      onChange={handleChange}
                      error={touched.name && Boolean(errors.name)}
                      helperText={touched.name && errors.name}
                      required
                      sx={(theme) => ({
                        '& .MuiFormLabel-asterisk': {
                          color: theme.palette.error.main,
                        },
                      })}
                    />
                    <TextField
                      label={t('createResume.references.fields.titleRole')}
                      name="title_or_role"
                      value={values.title_or_role}
                      onChange={handleChange}
                    />

                    <TextField
                      label={t('createResume.references.fields.employer')}
                      name="employer"
                      value={values.employer}
                      onChange={handleChange}
                    />
                    <TextField
                      label={t('createResume.references.fields.phone')}
                      name="phone_number"
                      value={values.phone_number}
                      onChange={handleChange}
                      error={touched.phone_number && Boolean(errors.phone_number)}
                      helperText={touched.phone_number && errors.phone_number}
                    />
                    <TextField
                      label={t('createResume.references.fields.email')}
                      name="email"
                      value={values.email}
                      onChange={handleChange}
                      error={touched.email && Boolean(errors.email)}
                      helperText={touched.email && errors.email}
                    />
                  </Stack>
                </DialogContent>
                <DialogActions>
                  <Button onClick={handleCloseDialog}>{t('createResume.references.dialog.cancel')}</Button>
                  <Button type="submit" variant="contained">
                    {editingItem.index !== -1
                      ? t('createResume.references.dialog.save')
                      : t('createResume.references.dialog.add')}
                  </Button>
                </DialogActions>
              </Form>
            )}
          </Formik>
        </Box>
      </Dialog>
    </Box>
  );
}
